import React, { useEffect } from 'react';
import { useSiteConfig } from '../context/SiteConfigContext';
import { DEFAULT_SITE_NAME } from '../constants/brand';
import { CANONICAL_ORIGIN } from '../constants/site';

const DEFAULT_IMAGE = '/assets/img/ridersoficarus.jpg';

export type SeoHeadProps = {
    title: string;
    description: string;
    path?: string;
    keywords?: string;
    image?: string;
    type?: 'website' | 'article';
    jsonLd?: Record<string, unknown> | Record<string, unknown>[];
    noSuffix?: boolean;
    /** Evita indexação (ex.: página vazia, erro, carregando). */
    noindex?: boolean;
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.content = content;
}

function upsertLink(rel: string, href: string) {
    if (!href) return;
    let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
    if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        document.head.appendChild(el);
    }
    el.href = href;
}

function upsertJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
    const id = 'seo-json-ld';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.type = 'application/ld+json';
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(Array.isArray(data) ? data : [data]);
}

const SeoHead: React.FC<SeoHeadProps> = ({
    title,
    description,
    path = '/',
    keywords,
    image = DEFAULT_IMAGE,
    type = 'website',
    jsonLd,
    noSuffix = false,
    noindex = false,
}) => {
    const { config } = useSiteConfig();
    const siteName = config.general?.siteName?.trim() || DEFAULT_SITE_NAME;
    const fullTitle = noSuffix ? title : `${title} | ${siteName}`;
    const canonicalPath = path.startsWith('/') ? path : `/${path}`;
    const canonical =
        canonicalPath === '/' ? `${CANONICAL_ORIGIN}/` : `${CANONICAL_ORIGIN}${canonicalPath}`;
    const imageUrl = image.startsWith('http') ? image : `${CANONICAL_ORIGIN}${image}`;

    useEffect(() => {
        document.title = fullTitle;
        upsertMeta('name', 'description', description);
        upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
        if (keywords) upsertMeta('name', 'keywords', keywords);

        upsertMeta('property', 'og:title', fullTitle);
        upsertMeta('property', 'og:description', description);
        upsertMeta('property', 'og:type', type);
        upsertMeta('property', 'og:url', canonical);
        upsertMeta('property', 'og:image', imageUrl);
        upsertMeta('property', 'og:site_name', siteName);

        upsertMeta('name', 'twitter:card', 'summary_large_image');
        upsertMeta('name', 'twitter:title', fullTitle);
        upsertMeta('name', 'twitter:description', description);
        upsertMeta('name', 'twitter:image', imageUrl);

        upsertLink('canonical', canonical);

        if (jsonLd) upsertJsonLd(jsonLd);
    }, [fullTitle, description, keywords, canonical, imageUrl, type, siteName, jsonLd, noindex]);

    return null;
};

export default SeoHead;
